import { Link } from 'react-router-dom';
import { BarChart } from '../../components/ui/BarChart';
import { enumLabel } from '../../lib/labels';
import { formatMoney } from '../../lib/money';

export interface PayableReport {
  unpaidPence: number;
  paidPence: number;
  spendPence: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  supplierRemainingPence: number;
  paymentsOutPence: number;
}

export interface PositionReport {
  invoicedNetPence: number;
  spendPence: number;
  contributionPence: number;
  unpaidInvoicesPence: number;
  unpaidBillsPence: number;
  committedPence: number;
  supplierRemainingPence: number;
  quotedPence: number;
  budgetPence: number;
}

function bars(record: Record<string, number>) {
  return Object.entries(record).filter(([, value]) => value > 0).map(([label, value]) => ({ label: enumLabel(label), value }));
}

function Card({ label, value, hint, to }: { label: string; value: string; hint?: string; to?: string }) {
  const body = (
    <>
      <div className="text-xs uppercase tracking-wide text-[var(--bo-faint)]">{label}</div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
      {hint ? <div className="mt-1 text-xs text-[var(--bo-muted)]">{hint}</div> : null}
    </>
  );
  const className = 'rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4';
  return to ? <Link to={to} className={className}>{body}</Link> : <div className={className}>{body}</div>;
}

export function PayablePanel({ data }: { data: PayableReport }) {
  return (
    <div className="space-y-4">
      <section className="grid gap-4 md:grid-cols-4">
        <Card label="Unpaid bills" value={formatMoney(data.unpaidPence)} hint="Draft, approved, scheduled, on hold, and mismatch" to="/bills" />
        <Card label="Paid bills" value={formatMoney(data.paidPence)} />
        <Card label="Spend" value={formatMoney(data.spendPence)} hint="Bills in these dates, in pounds" />
        <Card label="Left on supplier orders" value={formatMoney(data.supplierRemainingPence)} hint="All suppliers. Dates do not change this." to="/supplier-purchase-orders" />
        <Card label="Payments out" value={formatMoney(data.paymentsOutPence)} to="/payments-out" />
      </section>
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <h2 className="mb-3 text-sm font-semibold">Unpaid bills by status</h2>
          <BarChart rows={bars(data.byStatus)} />
        </div>
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <h2 className="mb-3 text-sm font-semibold">Spend by expense type</h2>
          <BarChart rows={bars(data.byType)} />
        </div>
      </section>
    </div>
  );
}

export function PositionPanel({ data }: { data: PositionReport }) {
  return (
    <div className="space-y-4">
      <section className="grid gap-4 md:grid-cols-4">
        <Card label="Invoiced net" value={formatMoney(data.invoicedNetPence)} />
        <Card label="Spend" value={formatMoney(data.spendPence)} />
        <Card label="Contribution" value={formatMoney(data.contributionPence)} hint="Invoiced net minus spend" />
        <Card label="Budget" value={formatMoney(data.budgetPence)} hint="Budget for these dates, not spend" to="/budgets" />
        <Card label="Unpaid invoices" value={formatMoney(data.unpaidInvoicesPence)} />
        <Card label="Unpaid bills" value={formatMoney(data.unpaidBillsPence)} />
        <Card label="Committed" value={formatMoney(data.committedPence)} hint="Left on customer orders" />
        <Card label="Supplier remaining" value={formatMoney(data.supplierRemainingPence)} hint="Left on supplier orders. Not filtered by customer." />
        <Card label="Quoted" value={formatMoney(data.quotedPence)} hint="Issued quotations with no linked order" />
      </section>
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <h2 className="mb-3 text-sm font-semibold">Invoiced net and spend</h2>
          <BarChart rows={[{ label: 'Invoiced net', value: data.invoicedNetPence }, { label: 'Spend', value: data.spendPence }]} />
        </div>
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <h2 className="mb-3 text-sm font-semibold">Unpaid invoices and unpaid bills</h2>
          <BarChart rows={[{ label: 'Unpaid invoices', value: data.unpaidInvoicesPence }, { label: 'Unpaid bills', value: data.unpaidBillsPence }]} />
        </div>
      </section>
    </div>
  );
}
